// src/pages/FamilyDetail.jsx - Vue détaillée avec gestion multi-clients
import React, { useState, useEffect, useMemo, useCallback } from "react";
import { supabase } from "../lib/supabaseClient";
import { useParams, useNavigate } from "react-router-dom";
import { generateFamilyPdf } from "../utils/FamilyDetailPdfExport";
import { 
   ChevronLeft, 
   Users, 
   FileText, 
   Shield, 
   Heart,
   TrendingUp,
   Package,
   Edit,
   Trash2,
   AlertCircle,
   UserPlus,
   Crown,
   Mail,
   Building2,
   X,
   Briefcase,
   Landmark,
   HandHeart,
   Banknote,
   Download
} from "lucide-react";

function FiscalTab({ familyId, currentRegime, onSave }) {
  const [selectedRegime, setSelectedRegime] = useState(currentRegime || "FR_PLUS_VALUE");

  const regimes = [
    {
      id: "FR_PLUS_VALUE",
      label: "France - Plus-value sur cession",
      description: "Taxation à 36,2% (19% IR + 17,2% PS) sur la plus-value. Abattement pour durée de détention : 5% par an de la 3e à 22e année.",
      optimal: "Idéal pour collections à long terme, transmission progressive"
    },
    {
      id: "FR_SUCCESSION",
      label: "France - Droits de succession",
      description: "Barème progressif 5-45% selon lien de parenté et montant. Abattement 100k€ ligne directe. Œuvres d'art exonérées si dation.",
      optimal: "Important pour planification successorale"
    },
    {
      id: "FR_ISF",
      label: "France - Exonération ISF/IFI",
      description: "Œuvres d'art, antiquités et objets de collection totalement exonérés d'IFI.",
      optimal: "Avantage patrimonial significatif"
    },
    {
      id: "FR_MECENAT",
      label: "France - Mécénat",
      description: "Réduction d'impôt 66% des dons (plafond 20% revenu). Donation avec usufruit : déduction immédiate + 0% succession.",
      optimal: "Optimisation via donations institutions"
    },
    {
      id: "BE_PLUS_VALUE",
      label: "Belgique - Plus-value",
      description: "Vente entre particuliers : 0% si non spéculatif. Vente professionnelle : 33% (sociétés).",
      optimal: "Favorable pour ventes occasionnelles"
    },
    {
      id: "CH_PLUS_VALUE",
      label: "Suisse - Plus-value",
      description: "Gain privé non imposable. Professionnel : impôt cantonal + fédéral (~20-25%).",
      optimal: "Très favorable pour particuliers"
    },
    {
      id: "UK_CGT",
      label: "UK - Capital Gains Tax",
      description: "20% sur plus-value (28% si résidence). Exemption 6k£/an. Chattels exemption 6k£.",
      optimal: "Favorable pour œuvres < 6k£"
    },
    {
      id: "OTHER",
      label: "Autre juridiction",
      description: "Régime fiscal spécifique à définir avec conseiller fiscal.",
      optimal: "Consultation expert requise"
    }
  ];

  const handleSave = () => {
    onSave(selectedRegime);
  };

  const currentRegimeDetails = regimes.find(r => r.id === selectedRegime);

  return (
    <div className="space-y-6">
      <div>
        <h3 className="text-xl font-serif text-[#1A1A1A] mb-2">
          Régime fiscal de référence
        </h3>
        <p className="text-sm text-[#666] font-light leading-relaxed">
          Définissez le régime fiscal par défaut pour cette famille. Ce paramètre sera utilisé dans les 
          exports de rapports (ventes, donations, succession) pour calculer les implications fiscales.
        </p>
      </div>

      {/* Sélection régime */}
      <div className="space-y-3">
        {regimes.map((regime) => (
          <div
            key={regime.id}
            onClick={() => setSelectedRegime(regime.id)}
            className={`border rounded-lg p-4 cursor-pointer transition-all ${
              selectedRegime === regime.id
                ? "border-[#1A1A1A] bg-[#FAFAFA]"
                : "border-[#E8E8E8] hover:border-[#999]"
            }`}
          >
            <div className="flex items-start gap-3">
              <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center flex-shrink-0 mt-0.5 ${
                selectedRegime === regime.id
                  ? "border-[#1A1A1A]"
                  : "border-[#CCC]"
              }`}>
                {selectedRegime === regime.id && (
                  <div className="w-3 h-3 rounded-full bg-[#1A1A1A]"></div>
                )}
              </div>

              <div className="flex-1">
                <h4 className="font-serif text-[#1A1A1A] mb-1">{regime.label}</h4>
                <p className="text-sm text-[#666] font-light leading-relaxed mb-2">
                  {regime.description}
                </p>
                <p className="text-xs text-emerald-600 font-light">
                  ✓ {regime.optimal}
                </p>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Aperçu régime sélectionné */}
      {currentRegimeDetails && (
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-6">
          <div className="flex items-start gap-3">
            <AlertCircle className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
            <div>
              <h4 className="font-serif text-[#1A1A1A] mb-2">
                Régime actuellement sélectionné : {currentRegimeDetails.label}
              </h4>
              <p className="text-sm text-[#666] font-light leading-relaxed">
                Ce régime sera appliqué par défaut dans tous les documents générés pour cette famille 
                (rapports de vente, plans de succession, donations). Vous pourrez toujours le modifier 
                pour un document spécifique si nécessaire.
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Actions */}
      <div className="flex items-center justify-between pt-6 border-t border-[#E8E8E8]">
        <p className="text-sm text-[#666] font-light">
          Les modifications seront enregistrées pour cette famille uniquement
        </p>
        <button
          onClick={handleSave}
          className="px-6 py-2.5 bg-[#1A1A1A] text-white rounded-lg hover:bg-[#333] transition-colors font-light"
        >
          Enregistrer
        </button>
      </div>
    </div>
  );
}

export default function FamilyDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  
  const [family, setFamily] = useState(null);
  const [foProfile, setFoProfile] = useState(null);
  const [familyClients, setFamilyClients] = useState([]);
  const [collections, setCollections] = useState([]);
  const [artworks, setArtworks] = useState([]);
  const [beneficiaries, setBeneficiaries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState(() => {
    // Ouvre automatiquement le bon onglet si ?rapport= est dans l'URL
    const rapportParam = new URLSearchParams(window.location.search).get("rapport");
    const validTabs = ["overview", "clients", "collections", "beneficiaries", "philanthropy", "fiscal"];
    const tabMap = { succession: "beneficiaries", philanthropy: "philanthropy", overview: "overview" };
    return tabMap[rapportParam] || (validTabs.includes(rapportParam) ? rapportParam : "overview");
  });
  const [fiscalRegime, setFiscalRegime] = useState(null); 
  
  // Modal d'ajout de client
  const [showAddClient, setShowAddClient] = useState(false);
  const [newClientEmail, setNewClientEmail] = useState("");
  const [newClientRole, setNewClientRole] = useState("member");
  const [addingClient, setAddingClient] = useState(false);

  const loadFamilyData = useCallback(async () => {
    setLoading(true);
    setError(null);
    // Collecteurs locaux pour triggerAutoExport (données fraîches avant setState)
    let clientsWithProfiles = [];
    let collectionsData = null;
    let artworksData = [];
    let beneficiariesData = null;

    try {
      // 1. Charger la famille
      const { data: familyData, error: familyError } = await supabase
        .from("families")
        .select("*")
        .eq("id", id)
        .maybeSingle();

      if (familyError) throw familyError;
      if (!familyData) throw new Error("Famille introuvable");

      setFamily(familyData);

      // Charger le profil du FO connecté (pour le branding PDF)
      let foProfileLocal = null;
      try {
        const { data: { user: foUser } } = await supabase.auth.getUser();
        if (foUser) {
          const { data: fo } = await supabase
            .from("profiles")
            .select("first_name, last_name, company")
            .eq("id", foUser.id)
            .single();
          if (fo) { setFoProfile(fo); foProfileLocal = fo; }
        }
      } catch (e) {
        console.warn("FO profile load skipped:", e?.message);
      }

// Charger régime fiscal
if (familyData.fiscal_regime) {
  setFiscalRegime(familyData.fiscal_regime);
}

      // 2. Charger TOUS les clients de la famille
      const { data: clientsData, error: clientsError } = await supabase
        .from("family_clients")
        .select(`
          id,
          client_id,
          role,
          is_primary,
          notes,
          created_at
        `)
        .eq("family_id", id);

      if (clientsError) {
        console.error("❌ Erreur chargement clients:", clientsError);
      }

      // 3. Charger les profils de chaque client
      if (clientsData && clientsData.length > 0) {
        const clientIds = clientsData.map(c => c.client_id);
        
        const { data: profilesData, error: profilesError } = await supabase
          .from("profiles")
          .select("*")
          .in("id", clientIds);

        if (profilesError) {
          console.error("❌ Erreur chargement profils:", profilesError);
        }

        // Fusionner les données clients + profils
        clientsWithProfiles = clientsData.map(client => {
          const profile = profilesData?.find(p => p.id === client.client_id);
          return {
            ...client,
            profile: profile || null
          };
        });

        setFamilyClients(clientsWithProfiles);

        // 4. Charger les collections de TOUS les clients
        let collectionsError;
        ({ data: collectionsData, error: collectionsError } = await supabase
          .from("collections")
          .select("*")
          .in("client_id", clientIds));
        if (!collectionsError && collectionsData) {
          setCollections(collectionsData);

          // 5. Charger les artworks de ces collections
          const collectionIds = collectionsData.map(c => c.id);
          if (collectionIds.length > 0) {
            const { data: artworksRes } = await supabase
              .from("artworks_secure")
              .select("*")
              .in("collection_id", collectionIds);
            artworksData = artworksRes || [];
            setArtworks(artworksData);
          }
        }



        // 6. Charger les bénéficiaires de tous les clients
        let beneficiariesError;
        ({ data: beneficiariesData, error: beneficiariesError } = await supabase
          .from("beneficiaries")
          .select("*")
          .in("client_id", clientIds));

        if (!beneficiariesError && beneficiariesData) {
          setBeneficiaries(beneficiariesData);
        }

      } else {
        setFamilyClients([]);
      }

      // Auto-export PDF si ?rapport= dans l'URL — données locales fraîches
      triggerAutoExport({
        foProfileLocal,
        familyLocal: familyData,
        familyClientsLocal: clientsWithProfiles || [],
        artworksLocal: artworksData || [],
        collectionsLocal: collectionsData || [],
        beneficiariesLocal: beneficiariesData || [],
      });

    } catch (err) {
      console.error("❌ Erreur chargement famille:", err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    loadFamilyData();
  }, [loadFamilyData]);

  // Déclenche le PDF auto si ?rapport= dans l'URL
  // Appelé depuis loadFamilyData avec les données locales fraîches (pas le state)
  const triggerAutoExport = (localData) => {
    const rapportAuto = new URLSearchParams(window.location.search).get("rapport");
    if (!rapportAuto) return;
    const { foProfileLocal, familyLocal, familyClientsLocal, artworksLocal, collectionsLocal, beneficiariesLocal } = localData;
    // Nettoyer l'URL immédiatement pour éviter re-trigger
    const url = new URL(window.location.href);
    url.searchParams.delete("rapport");
    window.history.replaceState({}, "", url.toString());
    // Générer le PDF avec les données fraîches
    generateFamilyPdf({
      foProfile: foProfileLocal,
      family: familyLocal,
      familyClients: familyClientsLocal,
      artworks: artworksLocal,
      collections: collectionsLocal,
      beneficiaries: beneficiariesLocal,
      reportType: rapportAuto,
    });
  };

  // Calculer les statistiques
  const stats = useMemo(() => {
    const totalValue = artworks.reduce((sum, art) => {
      const value = parseFloat(art.estimated_value || art.value || 0);
      return sum + value;
    }, 0);

    return {
      totalClients: familyClients.length,
      totalBeneficiaries: beneficiaries.length,
      totalCollections: collections.length,
      totalArtworks: artworks.length,
      totalValue
    };
  }, [familyClients, beneficiaries, collections, artworks]);

  // Ajouter un client
  const handleAddClient = async () => {
    if (!newClientEmail.trim()) {
      alert("Veuillez entrer un email");
      return;
    }

    setAddingClient(true);
    try {
      // 1. Trouver le profil par email
      const { data: profileId, error: lookupError } = await supabase.rpc(
        "get_profile_id_by_email",
        { p_email: newClientEmail.trim() }
      );

      if (lookupError) throw lookupError;
      if (!profileId) throw new Error("Aucun profil trouvé avec cet email");

      // 2. Vérifier si déjà client de cette famille
      const existingClient = familyClients.find(c => c.client_id === profileId);
      if (existingClient) {
        alert("Cette personne est déjà cliente de cette famille");
        return;
      }

      // 3. Ajouter le client
      const { error: insertError } = await supabase
        .from("family_clients")
        .insert({
          family_id: id,
          client_id: profileId,
          role: newClientRole,
          is_primary: familyClients.length === 0 // Premier client = primary
        });

      if (insertError) throw insertError;

      // 4. Recharger les données
      await loadFamilyData();
      
      // 5. Fermer le modal
      setShowAddClient(false);
      setNewClientEmail("");
      setNewClientRole("member");

    } catch (err) {
      console.error("❌ Erreur ajout client:", err);
      alert("Erreur : " + err.message);
    } finally {
      setAddingClient(false);
    }
  };

  // Retirer un client
  const handleRemoveClient = async (clientLinkId, clientName) => {
    if (!window.confirm(`Retirer ${clientName} de cette famille ?\n\nSes collections resteront intactes.`)) {
      return;
    }

    try {
      const { error } = await supabase
        .from("family_clients")
        .delete()
        .eq("id", clientLinkId);

      if (error) throw error;

      await loadFamilyData();
    } catch (err) {
      console.error("❌ Erreur suppression client:", err);
      alert("Erreur : " + err.message);
    }
  };

  // Définir comme contact principal
  const handleSetPrimary = async (clientLinkId) => {
    try {
      // 1. Retirer le flag primary de tous les autres
      await supabase
        .from("family_clients")
        .update({ is_primary: false })
        .eq("family_id", id);

      // 2. Définir ce client comme primary
      const { error } = await supabase
        .from("family_clients")
        .update({ is_primary: true })
        .eq("id", clientLinkId);

      if (error) throw error;

      await loadFamilyData();
    } catch (err) {
      console.error("❌ Erreur définition primary:", err);
      alert("Erreur : " + err.message);
    }
  };

  // Supprimer la famille
  const handleDeleteFamily = async () => {
    if (!window.confirm(
      `Supprimer la famille "${family.label}" ?\n\n` +
      `⚠️ Cette action supprimera uniquement la famille.\n` +
      `Les clients et leurs collections resteront intacts.`
    )) {
      return;
    }

    try {
      const { error: deleteError } = await supabase
        .from("families")
        .delete()
        .eq("id", id);

      if (deleteError) throw deleteError;

      navigate("/family-office/manage");
    } catch (err) {
      console.error("❌ Erreur suppression:", err);
      alert("Erreur lors de la suppression : " + err.message);
    }
  };

  // Export PDF — rapport blanc avec branding Family Office
  // Défini ici (FamilyDetail) pour accéder à activeTab, foProfile, beneficiaries
  // typeOverride : permet d'appeler depuis le header avec un type précis
  const exportPdf = (typeOverride) => {
    const reportType = typeOverride || (
      activeTab === "philanthropy" ? "philanthropy"
      : activeTab === "beneficiaries" ? "succession"
      : "overview"
    );

    generateFamilyPdf({
      foProfile,
      family,
      familyClients,
      artworks,
      collections,
      beneficiaries,
      reportType,
    });
  };

const handleSaveFiscalRegime = async (regime) => {
  try {
    const { error } = await supabase
      .from("families")
      .update({ fiscal_regime: regime })
      .eq("id", id);

    if (error) throw error;

    setFiscalRegime(regime);
    alert("Régime fiscal sauvegardé");
  } catch (error) {
    console.error("Erreur sauvegarde régime:", error);
    alert("Erreur lors de la sauvegarde");
  }
};
  if (loading) {

    return (
      <div className="min-h-screen bg-[#FAFAF8] flex items-center justify-center">
        <div className="text-center">
          <div className="inline-block w-12 h-12 border-2 border-[#E8E8E8] border-t-[#1A1A1A] 
                         rounded-full animate-spin" />
          <p className="mt-4 text-sm text-[#999]">Chargement de la famille...</p>
        </div>
      </div>
    );
  }

  if (error || !family) {


    return (
      <div className="min-h-screen bg-[#FAFAF8] p-8">
        <div className="max-w-7xl mx-auto">
          <div className="bg-red-50 border border-red-200 rounded p-6 flex items-start gap-3">
            <AlertCircle className="text-red-600 shrink-0" size={20} />
            <div>
              <p className="text-sm text-red-800 font-medium">
                {error || "Famille introuvable"}
              </p>
              <button
                onClick={() => navigate("/family-office/dashboard")}
                className="mt-3 text-sm text-red-600 hover:text-red-800 underline"
              >
                ← Retour au dashboard
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#FAFAF8]">
      {/* Header */}
      <div className="border-b border-[#E8E8E8] bg-white">
        <div className="max-w-7xl mx-auto px-8 py-6">
          <button
            onClick={() => navigate("/family-office/dashboard")}
            className="flex items-center gap-2 text-sm text-[#666] hover:text-[#1A1A1A] 
                     transition-colors mb-4"
          >
            <ChevronLeft size={16} />
            Retour au dashboard
          </button>

          <div className="flex items-start justify-between">
            <div>
              <div className="flex items-center gap-3 mb-2">
                <h1 className="text-3xl font-light text-[#1A1A1A]">{family.label}</h1>
                <span
                  className={`px-3 py-1 rounded text-sm font-light
                  ${family.status === "active"
                    ? "bg-emerald-50 text-emerald-700"
                    : "bg-amber-50 text-amber-700"
                  }`}
                >
                  {family.status}
                </span>
              </div>
              
              <div className="flex items-center gap-6 text-sm text-[#666]">
                <span className="flex items-center gap-1">
                  <Users size={14} />
                  {stats.totalClients} client{stats.totalClients > 1 ? "s" : ""}
                </span>
                <span className="flex items-center gap-1">
                  <Heart size={14} />
                  {stats.totalBeneficiaries} bénéficiaire{stats.totalBeneficiaries > 1 ? "s" : ""}
                </span>
                {family.created_at && (
                  <span>
                    Créée le {new Date(family.created_at).toLocaleDateString("fr-FR", {
                      day: "numeric",
                      month: "long",
                      year: "numeric"
                    })}
                  </span>
                )}
              </div>

              {family.notes && (
                <p className="mt-3 text-sm text-[#666] italic">{family.notes}</p>
              )}
            </div>

            <div className="flex items-center gap-2">
              {/* Rapport PDF — dropdown des 3 types */}
              <div className="relative group">
                <button
                  className="flex items-center gap-2 px-4 py-2 border border-[#C9A961] text-[#C9A961] rounded text-sm hover:bg-[#C9A961] hover:text-white transition-all"
                >
                  <Download size={16} />
                  Générer un rapport
                </button>
                <div className="absolute right-0 top-full mt-1 w-56 bg-white border border-[#E8E8E8] rounded-lg shadow-lg z-10 hidden group-hover:block">
                  {[
                    { type: "overview",     label: "Rapport patrimonial" },
                    { type: "succession",   label: "Rapport successoral" },
                    { type: "philanthropy", label: "Stratégie de mécénat" },
                  ].map(({ type, label }) => (
                    <button
                      key={type}
                      onClick={() => exportPdf(type)}
                      className="w-full text-left px-4 py-2.5 text-sm text-[#1A1A1A] hover:bg-[#F9F7F4] first:rounded-t-lg last:rounded-b-lg transition-colors"
                    >
                      {label}
                    </button>
                  ))}
                </div>
              </div>

              <button
                onClick={() => setShowAddClient(true)}
                className="flex items-center gap-2 px-4 py-2 bg-[#1A1A1A] text-white rounded
                         text-sm hover:bg-[#333] transition-all"
              >
                <UserPlus size={16} />
                Ajouter un client
              </button>
              
              <button
                className="p-2 border border-[#E8E8E8] rounded hover:bg-[#F5F5F5] transition-all"
                title="Modifier"
              >
                <Edit size={16} />
              </button>
              
              <button
                onClick={handleDeleteFamily}
                className="p-2 border border-[#E8E8E8] rounded hover:bg-red-50 
                         hover:text-red-600 hover:border-red-200 transition-all"
                title="Supprimer"
              >
                <Trash2 size={16} />
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Statistiques */}
      <div className="max-w-7xl mx-auto px-8 py-8">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
          <div className="bg-white rounded border border-[#E8E8E8] p-6">
            <div className="flex items-center gap-3 mb-2">
              <Users className="text-blue-600" size={20} />
              <h3 className="text-sm text-[#999]">Clients</h3>
            </div>
            <p className="text-3xl font-light text-[#1A1A1A]">{stats.totalClients}</p>
          </div>

          <div className="bg-white rounded border border-[#E8E8E8] p-6">
            <div className="flex items-center gap-3 mb-2">
              <Package className="text-amber-600" size={20} />
              <h3 className="text-sm text-[#999]">Collections</h3>
            </div>
            <p className="text-3xl font-light text-[#1A1A1A]">{stats.totalCollections}</p>
            <p className="text-xs text-[#999] mt-1">{stats.totalArtworks} œuvres</p>
          </div>

          <div className="bg-white rounded border border-[#E8E8E8] p-6">
            <div className="flex items-center gap-3 mb-2">
              <TrendingUp className="text-emerald-600" size={20} />
              <h3 className="text-sm text-[#999]">Valeur totale</h3>
            </div>
            <p className="text-3xl font-light text-[#1A1A1A]">
              {new Intl.NumberFormat("fr-FR", {
                style: "currency",
                currency: "EUR",
                notation: "compact"
              }).format(stats.totalValue)}
            </p>
          </div>

          <div className="bg-white rounded border border-[#E8E8E8] p-6">
            <div className="flex items-center gap-3 mb-2">
              <Heart className="text-rose-600" size={20} />
              <h3 className="text-sm text-[#999]">Bénéficiaires</h3>
            </div>
            <p className="text-3xl font-light text-[#1A1A1A]">{stats.totalBeneficiaries}</p>
          </div>
        </div>

        {/* Onglets */}
        <div className="bg-white rounded border border-[#E8E8E8] overflow-hidden">
          <div className="border-b border-[#E8E8E8] px-8 py-4 flex gap-6">
            {[
              { id: "overview", label: "Vue d'ensemble", icon: FileText },
              { id: "clients", label: `Clients (${familyClients.length})`, icon: Users },
              { id: "collections", label: `Collections (${collections.length})`, icon: Package },
              { id: "beneficiaries", label: `Bénéficiaires (${beneficiaries.length})`, icon: Heart },
	      { id: "philanthropy", label: "Mécénat", icon: Landmark },
              { id: "fiscal", label: "Préférences fiscales", icon: Banknote } 
            ].map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`flex items-center gap-2 px-4 py-2 text-sm font-light rounded transition-all
                  ${activeTab === tab.id
                    ? "bg-[#1A1A1A] text-white"
                    : "text-[#666] hover:bg-[#F5F5F5]"
                  }`}
              >
                <tab.icon size={16} />
                {tab.label}
              </button>
            ))}
          </div>

          <div className="p-8">
            {activeTab === "overview" && <OverviewTab family={family} stats={stats} />}
            
            {activeTab === "clients" && (
              <ClientsTab 
                clients={familyClients}
                collections={collections}
                artworks={artworks}
                onRemove={handleRemoveClient}
                onSetPrimary={handleSetPrimary}
              />
            )}
            
            {activeTab === "collections" && (
              <CollectionsTab 
                collections={collections}
                artworks={artworks}
                familyClients={familyClients}
              />
            )}
            
            {activeTab === "beneficiaries" && (
              <BeneficiariesTab 
                beneficiaries={beneficiaries}
                familyClients={familyClients}
              />
            )}
            {activeTab === "philanthropy" && (
              <PhilanthropyTab
                family={family}
                familyClients={familyClients}
                collections={collections}
                artworks={artworks}
                onExportPdf={exportPdf}
              />
            )}
{activeTab === "fiscal" && (
  <FiscalTab 
    familyId={id}
    currentRegime={fiscalRegime}
    onSave={handleSaveFiscalRegime}
  />
)}
          </div>
        </div>
      </div>

      {/* Modal ajout client */}
      {showAddClient && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg max-w-md w-full p-6">
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-lg font-light text-[#1A1A1A]">Ajouter un client</h3>
              <button
                onClick={() => setShowAddClient(false)}
                className="p-2 hover:bg-[#F5F5F5] rounded transition-all"
              >
                <X size={20} />
              </button>
            </div>

            <div className="space-y-4">
              <div>
                <label className="block text-sm text-[#666] mb-2">
                  Email du client
                </label>
                <input
                  type="email"
                  value={newClientEmail}
                  onChange={(e) => setNewClientEmail(e.target.value)}
                  placeholder="client@example.com"
                  className="w-full border border-[#E8E8E8] rounded px-4 py-3 text-sm
                           focus:outline-none focus:ring-2 focus:ring-[#1A1A1A]"
                  disabled={addingClient}
                />
              </div>

              <div>
                <label className="block text-sm text-[#666] mb-2">
                  Rôle
                </label>
                <select
                  value={newClientRole}
                  onChange={(e) => setNewClientRole(e.target.value)}
                  className="w-full border border-[#E8E8E8] rounded px-4 py-3 text-sm
                           focus:outline-none focus:ring-2 focus:ring-[#1A1A1A]"
                  disabled={addingClient}
                >
                  <option value="owner">Propriétaire principal</option>
                  <option value="member">Membre de la famille</option>
                </select>
              </div>

              <div className="flex gap-3 pt-4">
                <button
                  onClick={() => setShowAddClient(false)}
                  className="flex-1 px-4 py-3 border border-[#E8E8E8] rounded text-sm
                           hover:bg-[#F5F5F5] transition-all"
                  disabled={addingClient}
                >
                  Annuler
                </button>
                <button
                  onClick={handleAddClient}
                  className="flex-1 px-4 py-3 bg-[#1A1A1A] text-white rounded text-sm
                           hover:bg-[#333] transition-all disabled:opacity-50"
                  disabled={addingClient || !newClientEmail.trim()}
                >
                  {addingClient ? "Ajout..." : "Ajouter"}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// Onglet Vue d'ensemble
function OverviewTab({ family, stats }) {
  return (
    <div className="space-y-6">
      <h3 className="text-lg font-light text-[#1A1A1A]">Vue d'ensemble</h3>
      
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div className="border border-[#E8E8E8] rounded p-6">
          <h4 className="text-sm text-[#999] mb-4">Structure familiale</h4>
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <span className="text-sm text-[#666]">Clients membres</span>
              <span className="text-lg font-light">{stats.totalClients}</span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-sm text-[#666]">Bénéficiaires</span>
              <span className="text-lg font-light">{stats.totalBeneficiaries}</span>
            </div>
          </div>
        </div>

        <div className="border border-[#E8E8E8] rounded p-6">
          <h4 className="text-sm text-[#999] mb-4">Patrimoine artistique</h4>
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <span className="text-sm text-[#666]">Collections</span>
              <span className="text-lg font-light">{stats.totalCollections}</span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-sm text-[#666]">Œuvres</span>
              <span className="text-lg font-light">{stats.totalArtworks}</span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-sm text-[#666]">Valeur</span>
              <span className="text-lg font-light">
                {new Intl.NumberFormat("fr-FR", {
                  style: "currency",
                  currency: "EUR",
                  notation: "compact"
                }).format(stats.totalValue)}
              </span>
            </div>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-2 gap-4 mt-6">
        <div className="border border-[#E8E8E8] rounded p-4">
          <div className="flex items-center gap-2 text-[#666] mb-2">
            <Shield size={16} />
            <span className="text-sm">Assurance</span>
          </div>
          <p className="text-sm text-[#999]">Non configurée</p>
        </div>

        <div className="border border-[#E8E8E8] rounded p-4">
          <div className="flex items-center gap-2 text-[#666] mb-2">
            <Heart size={16} />
            <span className="text-sm">Plan de succession</span>
          </div>
          <p className="text-sm text-[#999]">
            {stats.totalBeneficiaries > 0 ? `${stats.totalBeneficiaries} bénéficiaire(s)` : "À définir"}
          </p>
        </div>
      </div>
    </div>
  );
}

// Onglet Clients
function ClientsTab({ clients, collections, artworks, onRemove, onSetPrimary }) {
  const clientsWithStats = useMemo(() => {
    return clients.map(client => {
      const clientCollections = collections.filter(c => c.client_id === client.client_id);
      const clientArtworks = artworks.filter(a => 
        clientCollections.some(c => c.id === a.collection_id)
      );
      const totalValue = clientArtworks.reduce((sum, art) => 
        sum + parseFloat(art.estimated_value || art.value || 0), 0
      );

      return {
        ...client,
        collectionsCount: clientCollections.length,
        artworksCount: clientArtworks.length,
        totalValue
      };
    });
  }, [clients, collections, artworks]);

  if (clients.length === 0) {
    return (
      <div className="text-center py-12">
        <Users className="mx-auto mb-4 text-[#CCC]" size={48} strokeWidth={1} />
        <p className="text-sm text-[#999]">Aucun client dans cette famille</p>
      </div>
    );
  }


  return (
    <div>
      <h3 className="text-lg font-light text-[#1A1A1A] mb-6">
        Clients membres ({clients.length})
      </h3>
      
      <div className="space-y-4">
        {clientsWithStats.map((client) => {
          const clientName = client.profile
            ? (client.profile.company || 
               [client.profile.first_name, client.profile.last_name].filter(Boolean).join(" ") ||
               "Sans nom")
            : "Profil inconnu";

          return (
            <div
              key={client.id}
              className="border border-[#E8E8E8] rounded p-6 hover:border-[#1A1A1A] transition-all"
            >
              <div className="flex items-start justify-between mb-4">
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-2">
                    <h4 className="text-lg font-light text-[#1A1A1A]">{clientName}</h4>
                    {client.is_primary && (
                      <Crown className="text-amber-500" size={16} title="Contact principal" />
                    )}
                    <span className="text-xs text-[#999] px-2 py-1 bg-[#F5F5F5] rounded">
                      {client.role === 'owner' ? 'Propriétaire' : 'Membre'}
                    </span>
                  </div>
                  
                  {client.profile?.email && (
                    <div className="flex items-center gap-2 text-sm text-[#666] mb-3">
                      <Mail size={14} />
                      {client.profile.email}
                    </div>
                  )}

                  <div className="grid grid-cols-3 gap-4 text-sm">
                    <div>
                      <p className="text-[#999] mb-1">Collections</p>
                      <p className="font-light text-[#1A1A1A]">{client.collectionsCount}</p>
                    </div>
                    <div>
                      <p className="text-[#999] mb-1">Œuvres</p>
                      <p className="font-light text-[#1A1A1A]">{client.artworksCount}</p>
                    </div>
                    <div>
                      <p className="text-[#999] mb-1">Valeur</p>
                      <p className="font-light text-[#1A1A1A]">
                        {new Intl.NumberFormat("fr-FR", {
                          style: "currency",
                          currency: "EUR",
                          notation: "compact"
                        }).format(client.totalValue)}
                      </p>
                    </div>
                  </div>
                </div>

                <div className="flex items-center gap-2">
                  {!client.is_primary && (
                    <button
                      onClick={() => onSetPrimary(client.id)}
                      className="p-2 border border-[#E8E8E8] rounded hover:bg-amber-50 
                               hover:border-amber-200 transition-all"
                      title="Définir comme contact principal"
                    >
                      <Crown size={16} />
                    </button>
                  )}
                  
                  <button
                    onClick={() => onRemove(client.id, clientName)}
                    className="p-2 border border-[#E8E8E8] rounded hover:bg-red-50 
                             hover:text-red-600 hover:border-red-200 transition-all"
                    title="Retirer de la famille"
                  >
                    <Trash2 size={16} />
                  </button>
                </div>
              </div>

              {client.notes && (
                <div className="pt-4 border-t border-[#E8E8E8]">
                  <p className="text-sm text-[#666] italic">{client.notes}</p>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

// Onglet Collections
function CollectionsTab({ collections, artworks, familyClients }) {
  const collectionsWithDetails = useMemo(() => {
    return collections.map(collection => {
      const client = familyClients.find(c => c.client_id === collection.client_id);
      const collectionArtworks = artworks.filter(a => a.collection_id === collection.id);
      const totalValue = collectionArtworks.reduce((sum, art) => 
        sum + parseFloat(art.estimated_value || art.value || 0), 0
      );

      return {
        ...collection,
        client,
        artworkCount: collectionArtworks.length,
        totalValue
      };
    });
  }, [collections, artworks, familyClients]);

  if (collections.length === 0) {
    return (
      <div className="text-center py-12">
        <Package className="mx-auto mb-4 text-[#CCC]" size={48} strokeWidth={1} />
        <p className="text-sm text-[#999]">Aucune collection</p>
      </div>
    );
  }

  return (
    <div>
      <h3 className="text-lg font-light text-[#1A1A1A] mb-6">
        Collections ({collections.length})
      </h3>
      
      <div className="space-y-3">
        {collectionsWithDetails.map((collection) => {
          const clientName = collection.client?.profile
            ? (collection.client.profile.company || 
               [collection.client.profile.first_name, collection.client.profile.last_name]
                 .filter(Boolean).join(" "))
            : "Client inconnu";

          return (
            <div
              key={collection.id}
              className="border border-[#E8E8E8] rounded p-6 hover:border-[#1A1A1A] transition-all"
            >
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <h4 className="font-light text-[#1A1A1A] text-lg mb-2">{collection.name}</h4>
                  
                  <div className="flex items-center gap-4 text-sm text-[#666] mb-3">
                    <span className="flex items-center gap-1">
                      <Briefcase size={14} />
                      {clientName}
                    </span>
                    <span className="flex items-center gap-1">
                      <FileText size={14} />
                      {collection.artworkCount} œuvre{collection.artworkCount > 1 ? "s" : ""}
                    </span>
                  </div>

                  {collection.description && (
                    <p className="text-sm text-[#666] mb-3">{collection.description}</p>
                  )}
                </div>

                <div className="text-right">
                  <p className="text-sm text-[#999] mb-1">Valeur</p>
                  <p className="text-lg font-light text-[#1A1A1A]">
                    {new Intl.NumberFormat("fr-FR", {
                      style: "currency",
                      currency: "EUR",
                      notation: "compact"
                    }).format(collection.totalValue)}
                  </p>
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

// Onglet Bénéficiaires
function BeneficiariesTab({ beneficiaries, familyClients }) {
  const beneficiariesByClient = useMemo(() => {
    return beneficiaries.map(beneficiary => {
      const client = familyClients.find(c => c.client_id === beneficiary.client_id);
      return {
        ...beneficiary,
        client
      };
    });
  }, [beneficiaries, familyClients]);

  if (beneficiaries.length === 0) {
    return (
      <div className="text-center py-12">
        <Heart className="mx-auto mb-4 text-[#CCC]" size={48} strokeWidth={1} />
        <p className="text-sm text-[#999]">Aucun bénéficiaire défini</p>
      </div>
    );
  }

  return (
    <div>
      <h3 className="text-lg font-light text-[#1A1A1A] mb-6">
        Bénéficiaires ({beneficiaries.length})
      </h3>
      
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {beneficiariesByClient.map((beneficiary) => {
          const clientName = beneficiary.client?.profile
            ? (beneficiary.client.profile.company || 
               [beneficiary.client.profile.first_name, beneficiary.client.profile.last_name]
                 .filter(Boolean).join(" "))
            : "Client inconnu";

          return (
            <div
              key={beneficiary.id}
              className="border border-[#E8E8E8] rounded p-5 hover:border-[#1A1A1A] transition-all"
            >
              <div className="flex items-start gap-4">
                {beneficiary.imageUrl ? (
                  <img 
                    src={beneficiary.imageUrl} 
                    alt={beneficiary.name}
                    className="w-12 h-12 rounded-full object-cover bg-[#F5F5F5]"
                  />
                ) : (
                  <div className="w-12 h-12 rounded-full bg-[#F5F5F5] flex items-center justify-center">
                    <Heart size={20} className="text-[#999]" />
                  </div>
                )}
                
                <div className="flex-1">
                  <h4 className="font-light text-[#1A1A1A] mb-1">{beneficiary.name}</h4>
                  <p className="text-xs text-[#999] mb-2">{beneficiary.relation}</p>
                  
                  <div className="flex items-center gap-2 text-xs text-[#666]">
                    <Briefcase size={12} />
                    <span>De : {clientName}</span>
                  </div>
                  
                  {beneficiary.email && (
                    <div className="flex items-center gap-2 text-xs text-[#666] mt-1">
                      <Mail size={12} />
                      <span>{beneficiary.email}</span>
                    </div>
                  )}
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
// Onglet Mécénat & philanthropie — basé sur le client principal de la famille

function PhilanthropyTab({ family, familyClients, collections, artworks, onExportPdf }) {
  const [email, setEmail] = React.useState("—");
  const [note, setNote] = React.useState("");
  const [saving, setSaving] = React.useState(false);

  // 1) Sélection du client de référence: primary sinon premier
  const primary = React.useMemo(() => {
    if (!familyClients || familyClients.length === 0) return null;
    const p = familyClients.find((c) => c.is_primary) || familyClients[0];
    return p?.profile ? p : null;
  }, [familyClients]);

  const fullName = React.useMemo(() => {
    if (!primary?.profile) return "—";
    const fn = (primary.profile.first_name || "").trim();
    const ln = (primary.profile.last_name || "").trim();
    const joined = [fn, ln].filter(Boolean).join(" ");
    return joined || "—";
  }, [primary]);


  // 🧮 Synthèse "œuvres exceptionnelles / usufruit / nue-propriété"
  const summary = useMemo(() => {
    const exceptional = artworks.filter(a => a.is_exceptional);
    const usufructed = artworks.filter(a => a.ownership_status === 'usufruct');
    const bare = artworks.filter(a => a.ownership_status === 'bare_ownership');
  
    const totalExceptional = exceptional.reduce(
      (sum, a) => sum + (parseFloat(a.current_estimated_value) || parseFloat(a.valuation_estimated_value) || parseFloat(a.estimated_value) || parseFloat(a.value) || 0), 0
    );
    const totalUsufruct = usufructed.reduce(
      (sum, a) => sum + (parseFloat(a.current_estimated_value) || parseFloat(a.valuation_estimated_value) || parseFloat(a.estimated_value) || parseFloat(a.value) || 0), 0
    );
  
    return {
      exceptionalCount: exceptional.length,
      exceptionalValue: totalExceptional,
      usufructCount: usufructed.length,
      usufructValue: totalUsufruct,
      bareCount: bare.length
    };
  }, [artworks]);

  const country = primary?.profile?.country || null;
  const regime = primary?.profile?.fiscal_regime || "NONE";
  const isFR =
    (country || "").toUpperCase() === "FR" ||
    (regime || "").toUpperCase().startsWith("FR_");

  // 2) Email depuis auth.users via RPC (email n’est pas dans profiles)
  React.useEffect(() => {
    let ignore = false;
    (async () => {
      try {
        if (!primary?.client_id) return;
        const { data, error } = await supabase.rpc("get_user_email", { uid: primary.client_id });
        if (!ignore) setEmail(error ? "—" : (data || "—"));
      } catch {
        if (!ignore) setEmail("—");
      }
    })();
    return () => { ignore = true; };
  }, [primary?.client_id]);

  // 3) Leviers et éligibilités selon FR / régime
  const levers = React.useMemo(() => {
    return [
      {
        key: "loan",
        title: "Prêt d’œuvres à des institutions",
        icon: <Building2 size={18} />,
        enabled: true,
        details: [
          "Dépôt temporaire ou convention de longue durée",
          isFR ? "Frais de conservation/assurance/restauration potentiellement déductibles (à confirmer)" : "Frais potentiellement déductibles selon pays",
          "Visibilité et légitimation de la collection"
        ]
      },
      {
        key: "dation",
        title: "Dation en paiement (FR)",
        icon: <Landmark size={18} />,
        enabled: isFR,
        details: [
          "Règlement des droits de succession en œuvres",
          "Évaluation à la valeur de marché (pas de plus-value taxée)",
          "Œuvres exceptionnelles acceptées par musées nationaux"
        ]
      },
      {
        key: "foundation",
        title: "Fondation d’entreprise ou sous égide",
        icon: <Banknote size={18} />,
        enabled: true,
        details: [
          "Structure pérenne pour gérer la collection",
          "Déduction fiscale des apports (selon juridiction)",
          "Organisation d’expositions et prêts muséaux"
        ]
      },
      {
        key: "usufruct",
        title: "Don avec usufruit / nue-propriété (FR)",
        icon: <HandHeart size={18} />,
        enabled: isFR,
        details: [
          "Conservation de la jouissance de l’œuvre",
          "Réduction fiscale immédiate sur la valeur de la NP",
          "Transmission progressive sans fiscalité successorale"
        ]
      }
    ];
  }, [isFR]);

  // 4) Estimation de gain fiscal (indicative & pédagogique)
  const estimatedTaxBenefit = React.useMemo(() => {
    if (!isFR) return 0;
    if (regime === "FR_PLUS_VALUE") return 15000;
    if (regime === "FR_FORFAIT_6_5") return 5000;
    return 3000;
  }, [isFR, regime]);

  // 5) Enregistrement (facultatif) — table `philanthropy_strategies`
  const savePlan = async () => {
    if (!primary?.client_id) {
      alert("Aucun client de référence.");
      return;
    }
    setSaving(true);
    try {
      const recommendation = {
        family_id: family.id,
        client_id: primary.client_id,
        country,
        regime,
        levers: levers.map((l) => ({ key: l.key, enabled: l.enabled })),
        note
      };

      const { error } = await supabase.from("philanthropy_strategies").insert({
   	family_id: family.id,                // 🔹 lien famille
   	profile_id: primary.client_id,       // 🔹 lien client référent
   	country: country || "FR",            // sécurisation du champ
   	fiscal_regime: regime,
   	recommendation,
   	potential_tax_saving: estimatedTaxBenefit,
   	created_at: new Date().toISOString() // cohérent avec table Supabase
      });

      if (error) {
        console.warn("philanthropy_strategies insert skipped:", error.message);
        alert("Plan créé (non historisé — table indisponible).");
      } else {
    // ✅ Marquer le profil comme philanthropy actif
    try {
      const { error: updErr } = await supabase
        .from("profiles")
        .update({
          philanthropy_active: true,
          // si tu as une colonne timestamp dédiée, dé-commente :
          // philanthropy_activated_at: new Date().toISOString(),
          updated_at: new Date().toISOString()
        })
        .eq("id", primary.client_id);
      if (updErr) {
        console.warn("profiles.philanthropy_active update skipped:", updErr.message);
      }
    } catch (e) {
      console.warn("profiles update exception:", e);
    }
        alert("Plan créé et historisé.");
      }
	    } catch (e) {
      console.warn("savePlan:", e);
      alert("Plan créé (non historisé).");
    } finally {
      setSaving(false);
    }
  };


  const prettyRegime = (r) => {
    switch (r) {
      case "FR_FORFAIT_6_5": return "FR — Forfait 6,5% (>5k€)";
      case "FR_PLUS_VALUE":  return "FR — PV 19% + 17,2% (abattement)";
      case "UK_CGT_SIMPLE_20": return "UK — CGT ~20%";
      case "DE_PRIVATE_1Y_RULE": return "DE — Exonération > 1 an";
      default: return "Non sélectionné";
    }
  };

  if (!primary) {
    return (
      <div className="text-center py-12">
        <Landmark className="mx-auto mb-4 text-[#CCC]" size={48} strokeWidth={1} />
        <p className="text-sm text-[#999]">Aucun client n’est rattaché à cette famille.</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-start justify-between">
        <div>
          <h3 className="text-lg font-light text-[#1A1A1A]">Mécénat & philanthropie — {family.label}</h3>
          <p className="text-sm text-[#666]">
            Client de référence : <span className="font-medium">{fullName}</span> — {email}
          </p>
          <p className="text-sm text-[#666]">
            Pays : <span className="font-medium">{country || "—"}</span> · Régime : <span className="font-medium">{prettyRegime(regime)}</span>
          </p>
        </div>
	<div className="bg-white border rounded p-4 mb-6">
	  <h4 className="font-medium mb-3">Synthèse actuelle du patrimoine engagé</h4>
	  <ul className="text-sm text-gray-700 space-y-1">
	    <li>Œuvres exceptionnelles : {summary.exceptionalCount} — {summary.exceptionalValue.toLocaleString("fr-FR")} €</li>
	    <li>Œuvres en usufruit : {summary.usufructCount} — {summary.usufructValue.toLocaleString("fr-FR")} €</li>
	    <li>Œuvres en nue-propriété : {summary.bareCount}</li>
	  </ul>
	</div>

        <button
          onClick={onExportPdf}
          className="inline-flex items-center gap-2 px-3 py-2 border rounded hover:bg-gray-50"
          title="Exporter le plan en PDF"
        >
          <Download size={16} /> Export PDF
        </button>
      </div>

      <div id="fam-phila-plan" className="bg-white border border-[#E8E8E8] rounded p-6">
        <div className="flex items-center justify-between mb-4">
          <h4 className="text-base font-medium text-[#1A1A1A]">Synthèse des leviers</h4>
          <div className="text-sm text-[#666]">
            Estimation gain fiscal (indicatif) :{" "}
            <span className="font-medium">
              {estimatedTaxBenefit.toLocaleString("fr-FR")} €
            </span>
          </div>
        </div>

        <div className="grid md:grid-cols-2 gap-4">
          {levers.map((lev) => (
            <div
              key={lev.key}
              className={`p-4 border rounded ${lev.enabled ? "bg-gray-50" : "bg-white opacity-60"}`}
            >
              <div className="flex items-center gap-2 mb-2">
                {lev.icon}
                <div className="font-medium">{lev.title}</div>
                <span
                  className={`ml-auto text-xs px-2 py-0.5 rounded border ${
                    lev.enabled
                      ? "bg-emerald-50 text-emerald-700 border-emerald-200"
                      : "bg-gray-100 text-gray-500 border-gray-200"
                  }`}
                >
                  {lev.enabled ? "Éligible" : "Non applicable"}
                </span>
              </div>
              <ul className="list-disc ml-5 text-sm text-gray-700 space-y-1">
                {lev.details.map((d, i) => <li key={i}>{d}</li>)}
              </ul>
            </div>
          ))}
        </div>

        {/* 🧩 Carte explicative — Usufruit / Nue-propriété */}
        <div className="mt-8 bg-gradient-to-r from-gray-50 via-white to-gray-50 border border-[#E8E8E8] rounded-lg p-6">
          <h4 className="text-base font-medium text-[#1A1A1A] mb-4 flex items-center gap-2">
            <HandHeart size={18} className="text-rose-600" />
            Démembrement de propriété : usufruit & nue-propriété
          </h4>

          <div className="grid md:grid-cols-2 gap-6 text-sm text-[#444]">
            {/* Usufruitier */}
            <div className="p-4 border border-[#E8E8E8] rounded-md bg-white">
              <h5 className="font-semibold text-[#1A1A1A] mb-2">👑 Usufruitier</h5>
              <ul className="list-disc ml-5 space-y-1">
                <li>Droit d’utiliser l’œuvre et d’en percevoir les revenus (prêt, exposition, location).</li>
                <li>Conserve la jouissance de l’œuvre pendant toute la durée fixée.</li>
                <li>Ne peut pas vendre l’œuvre ni en disposer définitivement.</li>
              </ul>
              <p className="mt-3 text-xs text-[#777]">
                En pratique : l’usufruitier peut exposer l’œuvre, la prêter à un musée
                ou l’assurer à son nom, mais ne peut pas la vendre.
              </p>
            </div>

            {/* Nue-propriété */}
            <div className="p-4 border border-[#E8E8E8] rounded-md bg-white">
              <h5 className="font-semibold text-[#1A1A1A] mb-2">💎 Nu-propriétaire</h5>
              <ul className="list-disc ml-5 space-y-1">
                <li>Détient la valeur patrimoniale de l’œuvre.</li>
                <li>Ne peut ni l’utiliser ni la louer pendant l’usufruit.</li>
                <li>Récupère la pleine propriété à l’extinction de l’usufruit.</li>
              </ul>
              <p className="mt-3 text-xs text-[#777]">
                En pratique : le nu-propriétaire ne peut pas exposer ni prêter l’œuvre tant que l’usufruit est en cours.
              </p>
            </div>
          </div>

          <div className="mt-6 border-t pt-3 text-xs text-[#666]">
            <b>À noter :</b> dans le module Family Office, ces informations peuvent être synchronisées
            avec le profil du client afin d’indiquer qui détient l’usage ou la nue-propriété de chaque œuvre.
            Cela permettra de déterminer automatiquement les droits d’exposition, de prêt ou de location.
          </div>
        </div>
        <div className="mt-6">
          <label className="block text-sm font-medium mb-1">Note du Family Office</label>
          <textarea
            value={note}
            onChange={(e) => setNote(e.target.value)}
            rows={3}
            className="w-full border rounded px-3 py-2 focus:outline-none focus:ring-1 focus:ring-black"
            placeholder="Points d’attention, institutions cibles, étapes prochaines…"
          />
        </div>

        <div className="mt-6 text-xs text-[#666] border-t pt-3">
          <b>Disclaimer.</b> Estimations indicatives basées sur les informations disponibles dans ArtKiv.
          Vérifier les règles applicables avec un conseil fiscal / avocat ; les seuils et mécanismes varient selon la juridiction.
        </div>
      </div>

      <div className="flex gap-3">
        <button
          onClick={savePlan}
          disabled={saving}
          className="px-4 py-2 bg-[#1A1A1A] text-white rounded hover:bg-[#333] disabled:opacity-50"
        >
          {saving ? "Enregistrement…" : "Créer le plan"}
        </button>
        <button
          onClick={onExportPdf}
          className="px-4 py-2 border rounded hover:bg-gray-50"
        >
          Exporter le PDF
        </button>
      </div>
    </div>
  );
}